import * as React from 'react'
import * as PropTypes from 'prop-types'

import isElementContainedIn from './is-element-contained-in'

export interface IInjectedClickDisplayProps {
  innerRef: React.RefObject<HTMLElement>
  setVisible: (visible: boolean) => void
  onClick: (event: React.SyntheticEvent<Element, Event>, visible: boolean) => void
  visible: boolean
}

interface IClickDisplayProps {
  onClick?: (event: React.SyntheticEvent) => void
  onShow: () => void
  onHide: () => void
}

export function clickDisplay<TProps extends IInjectedClickDisplayProps>(WrappedComponent: React.ComponentType<TProps>) {
  return class ClickDisplay extends React.Component<
    IClickDisplayProps & Pick<TProps, Exclude<keyof TProps, keyof IInjectedClickDisplayProps>>
  > {
    mounted = false
    static propTypes = {
      onClick: PropTypes.func,
      onShow: PropTypes.func,
      onHide: PropTypes.func,
    }

    constructor(props: IClickDisplayProps & Pick<TProps, Exclude<keyof TProps, keyof IInjectedClickDisplayProps>>) {
      super(props)

      this.clickDisplay = React.createRef()
    }

    state = {
      visible: false,
    }

    componentDidMount() {
      this.mounted = true
    }

    componentWillUnmount() {
      this.mounted = false
      window.removeEventListener('click', this.handleGlobalClick)
    }

    handleClick = (event: React.MouseEvent, visible = true) => {
      const { onClick, onShow } = this.props
      onClick && onClick(event)

      if (visible) {
        window.requestAnimationFrame(() => window.addEventListener('click', this.handleGlobalClick))
        onShow && onShow()
      }

      this.mounted && this.setState({ visible })
    }

    handleGlobalClick = (event: MouseEvent) => {
      const { onHide } = this.props
      const clickDisplayElement = this.clickDisplay.current
      const isTargetInDisplay =
        clickDisplayElement && isElementContainedIn(event.target as HTMLElement, clickDisplayElement)

      if (!isTargetInDisplay) {
        this.mounted && this.setState({ visible: false })
        window.removeEventListener('click', this.handleGlobalClick)
        onHide && onHide()
      }
    }

    clickDisplay: React.RefObject<HTMLElement>

    render() {
      return (
        <WrappedComponent
          innerRef={this.clickDisplay}
          {...(this.props as any)}
          onClick={this.handleClick}
          setVisible={visibility => this.mounted && this.setState({ visible: visibility })}
          visible={this.state.visible}
        />
      )
    }
  }
}
